import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, X } from 'lucide-react';
import { METHOD_TYPES, DETAIL_FIELDS, WEEKDAYS, STATUS_OPTIONS } from './methodConfig';

const empty = {
  type: 'bank_account',
  label: '',
  country: '',
  currencies: '',         // CSV in the form; split on save
  instructions: '',
  details: {},
  limits: { min_amount: '', max_amount: '', daily_cap: '' },
  schedule: {},
  status: 'active',
};

const toPayload = (form) => ({
  type: form.type,
  label: form.label.trim(),
  country: (form.country || '').trim().toUpperCase(),
  currencies: (form.currencies || '')
    .split(/[,\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean),
  instructions: form.instructions || '',
  details: form.details || {},
  limits: {
    min_amount: form.limits.min_amount === '' ? null : Number(form.limits.min_amount),
    max_amount: form.limits.max_amount === '' ? null : Number(form.limits.max_amount),
    daily_cap:  form.limits.daily_cap  === '' ? null : Number(form.limits.daily_cap),
  },
  schedule: form.schedule || {},
  status: form.status,
});

const fromExisting = (m) => ({
  type: m.type,
  label: m.label || '',
  country: m.country || '',
  currencies: (m.currencies || []).join(', '),
  instructions: m.instructions || '',
  details: m.details || {},
  limits: {
    min_amount: m.limits?.min_amount ?? '',
    max_amount: m.limits?.max_amount ?? '',
    daily_cap:  m.limits?.daily_cap  ?? '',
  },
  schedule: m.schedule || {},
  status: m.status === 'archived' ? 'active' : (m.status || 'active'),
});

const PaymentMethodForm = ({ open, onClose, onSubmit, initial = null }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? fromExisting(initial) : empty);
  }, [open, initial]);

  const detailFields = useMemo(() => DETAIL_FIELDS[form.type] || [], [form.type]);

  const valid = form.label.trim().length >= 2 && /^[A-Za-z]{2}$/.test(form.country);

  const handleSubmit = async () => {
    if (!valid) {
      toast.error('Completa nombre y país (ISO-2, ej. UY)');
      return;
    }
    setBusy(true);
    try {
      await onSubmit(toPayload(form));
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el método');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="payment-method-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar método de pago' : 'Nuevo método de pago'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Type */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Tipo</label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, details: {} })} disabled={isEdit}>
              <SelectTrigger data-testid="pm-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHOD_TYPES.map((t) => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Label + country */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">Nombre interno</label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ej. BROU UY USD"
                data-testid="pm-form-label"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">País (ISO-2)</label>
              <Input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
                placeholder="UY / CU / ES"
                maxLength={2}
                data-testid="pm-form-country"
              />
            </div>
          </div>

          {/* Currencies */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Monedas aceptadas (separadas por coma)</label>
            <Input
              value={form.currencies}
              onChange={(e) => setForm({ ...form, currencies: e.target.value })}
              placeholder="USD, UYU"
              data-testid="pm-form-currencies"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Instrucciones para el gestor</label>
            <Textarea
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              placeholder="Explica claramente cómo se realiza el pago, qué adjuntar, plazos, etc."
              rows={3}
              data-testid="pm-form-instructions"
            />
          </div>

          {/* Type-specific detail fields */}
          {detailFields.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Datos del método</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {detailFields.map((f) => (
                  <div key={f.key}>
                    <label className="text-[10px] uppercase tracking-wide text-slate-500">
                      {f.label}{f.sensitive && <span className="ml-1 text-rose-500">(privado)</span>}
                    </label>
                    <Input
                      value={form.details?.[f.key] || ''}
                      onChange={(e) => setForm({ ...form, details: { ...form.details, [f.key]: e.target.value } })}
                      data-testid={`pm-form-detail-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Limits */}
          <div>
            <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Límites (USD)</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Mínimo</label>
                <Input
                  type="number" min="0"
                  value={form.limits.min_amount}
                  onChange={(e) => setForm({ ...form, limits: { ...form.limits, min_amount: e.target.value } })}
                  data-testid="pm-form-limit-min"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Máximo</label>
                <Input
                  type="number" min="0"
                  value={form.limits.max_amount}
                  onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_amount: e.target.value } })}
                  data-testid="pm-form-limit-max"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Tope diario</label>
                <Input
                  type="number" min="0"
                  value={form.limits.daily_cap}
                  onChange={(e) => setForm({ ...form, limits: { ...form.limits, daily_cap: e.target.value } })}
                  data-testid="pm-form-limit-daily"
                />
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div>
            <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Horarios (opcional · vacío = siempre disponible)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {WEEKDAYS.map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">{label}</span>
                  <Input
                    placeholder="09:00-18:00"
                    value={form.schedule?.[key] || ''}
                    onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, [key]: e.target.value } })}
                    data-testid={`pm-form-sched-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Estado</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="pm-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="pm-form-cancel">
            <X className="w-4 h-4 mr-1" /> Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={busy || !valid} className="bg-blue-600 hover:bg-blue-700" data-testid="pm-form-save">
            <Save className="w-4 h-4 mr-1" /> {busy ? 'Guardando…' : (isEdit ? 'Guardar cambios' : 'Crear método')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodForm;
